import { http, type HttpResponse } from 'interfaces'
import { revertTransformAddress, transformAddress } from 'services'
import { type PersonAddress } from '../../../../components'

interface ApiAddress {
  id: number
  personId: number
  primaryBuilding: string | null
  primaryRoom: string | null
  primaryStreetAddress: string
  primaryStreetAddressTwo: string | null
  primaryCity: string
  primaryState: {
    id: number
    desc: string | null
  }
  primaryCountry: {
    id: number
    country_desc: string | null
  }
  primaryZipcode: string
  secondaryBuilding: string | null
  secondaryRoom: string | null
  secondaryStreetAddress: string | null
  secondaryStreetAddressTwo: string | null
  secondaryCity: string | null
  secondaryState: {
    id: number
    desc: string | null
  }
  secondaryCountry: {
    id: number
    country_desc: string | null
  }
  secondaryZipcode: string | null
  mailingAddress: number
  displayOrder: number
  typeId: number
  addressType: {
    id: number
    name: string
  } | null
  phones: Array<{
    id: number
    addressId: number
    areaCode: string
    phoneNumber: string | null
    typeId: number
    extension: string
    displayOrder: number
    personId: number
  }> | null
}

function transformApiToPersonAddress (apiAddress: ApiAddress): PersonAddress {
  return {
    id: apiAddress.id,
    personId: apiAddress.personId,
    typeId: apiAddress.typeId,
    addressType: apiAddress.addressType,
    primary: transformAddress(apiAddress, 'primary'),
    secondary: transformAddress(apiAddress, 'secondary'),
    mailingAddress: apiAddress.mailingAddress === 2 ? 2 : 1,
    displayOrder: apiAddress.displayOrder,
    phones: (apiAddress.phones != null ? apiAddress.phones : []).map(phone => ({
      id: phone.id,
      personId: phone.personId,
      addressId: phone.addressId,
      countryCode: '1',
      areaCode: phone.areaCode ?? '',
      phoneNumber: phone.phoneNumber ?? '',
      extension: phone.extension ?? '',
      typeId: phone.typeId,
      displayOrder: phone.displayOrder
    }))
  }
}

export function transformPersonAddressToApi (address: PersonAddress): any {
  return {
    id: address.id,
    typeId: address.typeId,
    personId: address.personId,
    ...revertTransformAddress(address.primary, 'primary'),
    ...revertTransformAddress(address.secondary, 'secondary'),
    mailingAddress: address.mailingAddress,
    phones: address.phones.map(phone => ({
      ...phone,
      id: phone.id != null && phone.id < 0 ? 0 : phone.id
    })),
    displayOrder: address.displayOrder
  }
}

export async function getPersonAddresses (personId: number, roleId?: number): Promise<PersonAddress[]> {
  const params = roleId != null ? { role_id: roleId } : undefined
  return await http
    .get(`/api/v1/us/person/${personId}/address/`, { params })
    .then(response => response.data.map(transformApiToPersonAddress))
}

export async function createPersonAddress (personId: number, addressData: PersonAddress): Promise<HttpResponse<PersonAddress>> {
  return await http
    .post(`/api/v1/us/person/${personId}/address/`, transformPersonAddressToApi(addressData))
    .then(response => ({
      ...response.data,
      result: response.data.result != null ? transformApiToPersonAddress(response.data.result) : null
    }))
}

export async function updatePersonAddress (addressId: number, addressData: PersonAddress): Promise<HttpResponse<PersonAddress>> {
  return await http
    .put(`/api/v1/us/person/address/${addressId}/`, transformPersonAddressToApi(addressData))
    .then(response => ({
      ...response.data,
      result: response.data.result != null ? transformApiToPersonAddress(response.data.result) : null
    }))
}

export async function deletePersonAddress (addressId: number): Promise<HttpResponse<void>> {
  return await http
    .delete(`/api/v1/us/person/address/${addressId}/`)
    .then(response => response.data)
}
